import { LitElement, html, css } from 'lit';
import { designSystem, componentStyles } from '../styles/design-system.js';
import { iconStyles } from '../styles/icon-styles.js';

export class AgentsManager extends LitElement {
  static properties = {
    agents: { type: Array },
    selectedAgent: { type: Object },
    viewMode: { type: String },
    activeTab: { type: String },
    _toastVisible: { type: Boolean, state: true }
  };

  static styles = [
    designSystem,
    componentStyles,
    css`
      :host {
        display: block;
        animation: fadeIn var(--duration-slow) var(--ease-out) both;
      }

      /* ─── Page Header ─── */
      .page-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: var(--space-8);
      }

      .page-title {
        font-family: var(--font-display);
        font-size: var(--text-4xl);
        font-weight: var(--font-black);
        color: var(--color-text-primary);
        letter-spacing: -0.04em;
      }

      .header-actions {
        display: flex;
        align-items: center;
        gap: var(--space-3);
      }

      .view-toggle {
        display: flex;
        background: var(--color-bg-tertiary);
        border: 1px solid var(--color-border-primary);
        border-radius: var(--radius-md);
        overflow: hidden;
      }

      .view-toggle-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 36px;
        height: 34px;
        border: none;
        background: transparent;
        color: var(--color-text-tertiary);
        cursor: pointer;
        transition: all var(--transition-fast);
        font-size: var(--text-sm);
      }

      .view-toggle-btn:hover {
        color: var(--color-text-secondary);
        background: var(--color-bg-hover);
      }

      .view-toggle-btn.active {
        background: var(--color-bg-secondary);
        color: var(--color-accent);
        box-shadow: var(--shadow-xs);
      }

      /* ─── Agent Cards Grid ─── */
      .agents-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: var(--space-6);
        animation: slideUp var(--duration-slow) var(--ease-out) both;
      }

      .agents-grid.list-view {
        grid-template-columns: 1fr;
      }

      .agents-grid.list-view .agent-card {
        display: grid;
        grid-template-columns: auto 1fr auto;
        gap: var(--space-5);
        align-items: center;
      }

      .agents-grid.list-view .agent-card-body {
        display: flex;
        align-items: center;
        gap: var(--space-6);
      }

      .agents-grid.list-view .agent-stats-row {
        flex-direction: row;
      }

      .agents-grid.list-view .agent-card-footer {
        justify-content: flex-end;
      }

      .agent-card {
        background: rgba(255, 255, 255, 0.18);
        border: 1px solid rgba(255, 255, 255, 0.35);
        border-radius: var(--radius-xl);
        padding: var(--space-6);
        cursor: pointer;
        transition: all var(--transition-base);
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.08);
        position: relative;
        backdrop-filter: blur(8px) saturate(1.2);
        -webkit-backdrop-filter: blur(8px) saturate(1.2);
        overflow: hidden;
      }

      .agent-card:hover {
        box-shadow: var(--shadow-hover-lift);
        border-color: var(--color-border-secondary);
        transform: scale(1.01);
      }

      .agent-card-header {
        display: flex;
        align-items: flex-start;
        gap: var(--space-3);
        margin-bottom: var(--space-4);
      }

      .agent-avatar {
        width: 44px;
        height: 44px;
        border-radius: var(--radius-full);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: var(--text-lg);
        color: var(--color-text-inverse);
        flex-shrink: 0;
      }

      .agent-avatar.color-orange { background: var(--color-primary); box-shadow: 0 2px 8px rgba(245, 158, 11, 0.2); }
      .agent-avatar.color-blue { background: var(--color-accent); box-shadow: 0 2px 8px rgba(59, 130, 246, 0.2); }
      .agent-avatar.color-green { background: var(--color-success); box-shadow: 0 2px 8px rgba(34, 197, 94, 0.2); }
      .agent-avatar.color-purple { background: var(--color-info); box-shadow: 0 2px 8px rgba(139, 92, 246, 0.2); }

      .agent-info {
        flex: 1;
        min-width: 0;
      }

      .agent-name {
        font-family: var(--font-display);
        font-size: var(--text-base);
        font-weight: var(--font-bold);
        color: var(--color-text-primary);
        letter-spacing: var(--tracking-tight);
        line-height: var(--leading-tight);
        margin-bottom: var(--space-1);
      }

      .agent-meta {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        flex-wrap: wrap;
      }

      .agent-stats-row {
        display: flex;
        gap: var(--space-4);
        margin-bottom: var(--space-3);
      }

      .agent-stat-item {
        display: flex;
        flex-direction: column;
        gap: 2px;
        flex: 1;
      }

      .stat-value {
        font-family: var(--font-mono);
        font-size: var(--text-sm);
        font-weight: var(--font-semibold);
        color: var(--color-text-primary);
      }

      .stat-label {
        font-family: var(--font-body);
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
        letter-spacing: var(--tracking-wide);
      }

      .agent-card-footer {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding-top: var(--space-3);
        border-top: 1px solid var(--color-border-subtle);
      }

      .last-active {
        font-family: var(--font-body);
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
      }

      .voice-name {
        font-family: var(--font-body);
        font-size: var(--text-xs);
        color: var(--color-text-secondary);
        display: flex;
        align-items: center;
        gap: var(--space-1);
      }

      /* ─── Agent Detail Panel ─── */
      .detail-panel {
        animation: slideUp var(--duration-slow) var(--ease-out) both;
      }

      .detail-back-btn {
        display: inline-flex;
        align-items: center;
        gap: var(--space-2);
        padding: var(--space-2) var(--space-3);
        background: transparent;
        border: none;
        border-radius: var(--radius-md);
        font-family: var(--font-body);
        font-size: var(--text-sm);
        font-weight: var(--font-medium);
        color: var(--color-text-secondary);
        cursor: pointer;
        transition: all var(--transition-fast);
        margin-bottom: var(--space-4);
      }

      .detail-back-btn:hover {
        color: var(--color-primary);
        background: var(--color-primary-light);
      }

      .detail-header {
        display: flex;
        align-items: center;
        gap: var(--space-4);
        margin-bottom: var(--space-6);
      }

      .detail-avatar {
        width: 56px;
        height: 56px;
        border-radius: var(--radius-full);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: var(--text-xl);
        color: var(--color-text-inverse);
        flex-shrink: 0;
      }

      .detail-title-area {
        flex: 1;
      }

      .detail-title {
        font-family: var(--font-display);
        font-size: var(--text-2xl);
        font-weight: var(--font-bold);
        color: var(--color-text-primary);
        letter-spacing: var(--tracking-tight);
      }

      .detail-subtitle {
        font-family: var(--font-body);
        font-size: var(--text-sm);
        color: var(--color-text-tertiary);
        margin-top: 2px;
      }

      /* ─── Tab Bar ─── */
      .detail-tabs {
        display: flex;
        gap: var(--space-1);
        border-bottom: 1px solid var(--color-border-primary);
        margin-bottom: var(--space-6);
      }

      .detail-tab {
        padding: var(--space-3) var(--space-4);
        font-family: var(--font-body);
        font-size: var(--text-sm);
        font-weight: var(--font-medium);
        color: var(--color-text-tertiary);
        cursor: pointer;
        border: none;
        background: transparent;
        border-bottom: 2px solid transparent;
        margin-bottom: -1px;
        transition: all var(--transition-fast);
        white-space: nowrap;
      }

      .detail-tab:hover {
        color: var(--color-text-secondary);
      }

      .detail-tab.active {
        color: var(--color-primary);
        border-bottom-color: var(--color-primary);
        background: var(--color-primary-light);
        border-radius: var(--radius-md) var(--radius-md) 0 0;
      }

      /* ─── Overview Tab ─── */
      .metrics-row {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: var(--space-4);
        margin-bottom: var(--space-6);
      }

      .metric-card {
        background: rgba(255, 255, 255, 0.18);
        border: 1px solid rgba(255, 255, 255, 0.35);
        border-radius: var(--radius-xl);
        padding: var(--space-6);
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.08);
        backdrop-filter: blur(8px) saturate(1.2);
        -webkit-backdrop-filter: blur(8px) saturate(1.2);
        position: relative;
        overflow: hidden;
      }

      .metric-card .metric-label {
        margin-bottom: var(--space-2);
      }

      .metric-card .metric-value {
        font-size: var(--text-2xl);
      }

      .recent-calls-section {
        background: rgba(255, 255, 255, 0.18);
        border: 1px solid rgba(255, 255, 255, 0.35);
        border-radius: var(--radius-xl);
        padding: var(--space-6);
        margin-bottom: var(--space-6);
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.08);
        backdrop-filter: blur(8px) saturate(1.2);
        -webkit-backdrop-filter: blur(8px) saturate(1.2);
        position: relative;
        overflow: hidden;
      }

      .section-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: var(--space-4);
      }

      .call-list-item {
        display: flex;
        align-items: center;
        gap: var(--space-3);
        padding: var(--space-3) 0;
        border-bottom: 1px solid var(--color-border-subtle);
      }

      .call-list-item:last-child {
        border-bottom: none;
      }

      .call-icon-wrap {
        width: 32px;
        height: 32px;
        border-radius: var(--radius-md);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: var(--text-xs);
        flex-shrink: 0;
      }

      .call-icon-wrap.success {
        background: var(--color-success-bg);
        color: var(--color-success-text);
      }

      .call-icon-wrap.missed {
        background: var(--color-error-bg);
        color: var(--color-error-text);
      }

      .call-info {
        flex: 1;
        min-width: 0;
      }

      .call-number {
        font-family: var(--font-mono);
        font-size: var(--text-sm);
        color: var(--color-text-primary);
      }

      .call-time {
        font-family: var(--font-body);
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
      }

      .call-duration {
        font-family: var(--font-mono);
        font-size: var(--text-xs);
        color: var(--color-text-secondary);
      }

      /* ─── Activity Timeline ─── */
      .timeline {
        padding-left: var(--space-6);
        position: relative;
      }

      .timeline::before {
        content: '';
        position: absolute;
        left: 11px;
        top: 4px;
        bottom: 4px;
        width: 2px;
        background: var(--color-border-primary);
      }

      .timeline-item {
        position: relative;
        padding-bottom: var(--space-4);
        padding-left: var(--space-4);
      }

      .timeline-item::before {
        content: '';
        position: absolute;
        left: -19px;
        top: 6px;
        width: 8px;
        height: 8px;
        border-radius: var(--radius-full);
        background: var(--color-primary);
        border: 2px solid var(--color-bg-primary);
      }

      .timeline-item:last-child {
        padding-bottom: 0;
      }

      .timeline-content {
        font-family: var(--font-body);
        font-size: var(--text-sm);
        color: var(--color-text-secondary);
      }

      .timeline-time {
        font-family: var(--font-body);
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
        margin-top: 2px;
      }

      /* ─── Configuration Tab ─── */
      .config-section {
        margin-bottom: var(--space-8);
        background: rgba(255, 255, 255, 0.18);
        border: 1px solid rgba(255, 255, 255, 0.35);
        border-radius: var(--radius-xl);
        padding: var(--space-6);
        backdrop-filter: blur(8px) saturate(1.2);
        -webkit-backdrop-filter: blur(8px) saturate(1.2);
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.08);
        position: relative;
        overflow: hidden;
      }

      .config-section-title {
        font-family: var(--font-display);
        font-size: var(--text-lg);
        font-weight: var(--font-bold);
        color: var(--color-text-primary);
        letter-spacing: var(--tracking-tight);
        margin-bottom: var(--space-1);
      }

      .config-section-desc {
        font-family: var(--font-body);
        font-size: var(--text-sm);
        color: var(--color-text-tertiary);
        margin-bottom: var(--space-5);
      }

      .form-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: var(--space-4);
      }

      .form-grid.full {
        grid-template-columns: 1fr;
      }

      .form-group {
        display: flex;
        flex-direction: column;
        gap: var(--space-1\.5);
      }

      .form-label {
        font-family: var(--font-body);
        font-size: var(--text-sm);
        font-weight: var(--font-medium);
        color: var(--color-text-secondary);
      }

      .form-input,
      .form-select,
      .form-textarea {
        padding: var(--space-2) var(--space-3);
        background: var(--color-bg-secondary);
        border: 1px solid var(--color-border-primary);
        border-radius: var(--radius-md);
        color: var(--color-text-primary);
        font-family: var(--font-body);
        font-size: var(--text-sm);
        transition: all var(--transition-fast);
        line-height: var(--leading-normal);
      }

      .form-input:focus,
      .form-select:focus,
      .form-textarea:focus {
        outline: none;
        border-color: var(--color-primary);
        box-shadow: var(--shadow-glow-primary);
      }

      .form-textarea {
        min-height: 100px;
        resize: vertical;
        line-height: var(--leading-relaxed);
      }

      .form-hint {
        font-family: var(--font-body);
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
      }

      .char-count {
        font-family: var(--font-mono);
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
        text-align: right;
        margin-top: 2px;
      }

      .input-with-btn {
        display: flex;
        gap: var(--space-2);
        align-items: center;
      }

      .input-with-btn .form-select,
      .input-with-btn .form-input {
        flex: 1;
      }

      .play-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 36px;
        height: 36px;
        border-radius: var(--radius-md);
        border: 1px solid var(--color-border-primary);
        background: var(--color-bg-secondary);
        color: var(--color-primary);
        cursor: pointer;
        transition: all var(--transition-fast);
        flex-shrink: 0;
      }

      .play-btn:hover {
        background: var(--color-primary-light);
        border-color: var(--color-primary);
      }

      /* ─── Business Hours ─── */
      .hours-grid {
        display: flex;
        flex-direction: column;
        gap: var(--space-2);
      }

      .hours-row {
        display: grid;
        grid-template-columns: 48px 80px 1fr 1fr;
        gap: var(--space-3);
        align-items: center;
        padding: var(--space-2) var(--space-3);
        background: var(--color-bg-tertiary);
        border-radius: var(--radius-md);
        font-family: var(--font-body);
        font-size: var(--text-sm);
      }

      .hours-day {
        font-weight: var(--font-medium);
        color: var(--color-text-primary);
        font-size: var(--text-xs);
        text-transform: uppercase;
        letter-spacing: var(--tracking-wider);
      }

      .time-input {
        padding: var(--space-1\.5) var(--space-2);
        background: var(--color-bg-secondary);
        border: 1px solid var(--color-border-primary);
        border-radius: var(--radius-sm);
        color: var(--color-text-primary);
        font-family: var(--font-mono);
        font-size: var(--text-xs);
      }

      .time-input:focus {
        outline: none;
        border-color: var(--color-primary);
        box-shadow: var(--shadow-glow-primary);
      }

      /* ─── Slider ─── */
      .slider-group {
        display: flex;
        flex-direction: column;
        gap: var(--space-2);
      }

      .slider-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      .slider-value {
        font-family: var(--font-mono);
        font-size: var(--text-sm);
        font-weight: var(--font-semibold);
        color: var(--color-primary);
        background: var(--color-primary-light);
        padding: 2px var(--space-2);
        border-radius: var(--radius-sm);
      }

      input[type="range"] {
        -webkit-appearance: none;
        width: 100%;
        height: 6px;
        background: var(--color-bg-inset);
        border-radius: var(--radius-full);
        outline: none;
      }

      input[type="range"]::-webkit-slider-thumb {
        -webkit-appearance: none;
        width: 18px;
        height: 18px;
        border-radius: var(--radius-full);
        background: var(--color-primary);
        cursor: pointer;
        box-shadow: var(--shadow-sm);
        transition: all var(--transition-fast);
      }

      input[type="range"]::-webkit-slider-thumb:hover {
        transform: scale(1.15);
        box-shadow: var(--shadow-glow-primary);
      }

      /* ─── Toast ─── */
      .toast {
        position: fixed;
        bottom: var(--space-6);
        right: var(--space-6);
        background: var(--color-success);
        color: var(--color-text-inverse);
        padding: var(--space-3) var(--space-5);
        border-radius: var(--radius-lg);
        font-family: var(--font-body);
        font-size: var(--text-sm);
        font-weight: var(--font-medium);
        display: flex;
        align-items: center;
        gap: var(--space-2);
        box-shadow: var(--shadow-xl);
        z-index: var(--z-toast);
        transform: translateY(100px);
        opacity: 0;
        transition: all var(--duration-slow) var(--ease-out);
      }

      .toast.visible {
        transform: translateY(0);
        opacity: 1;
      }

      /* ─── Responsive ─── */
      @media (max-width: 1200px) {
        .agents-grid {
          grid-template-columns: repeat(2, 1fr);
        }
        .metrics-row {
          grid-template-columns: repeat(2, 1fr);
        }
      }

      @media (max-width: 768px) {
        .agents-grid {
          grid-template-columns: 1fr;
        }
        .metrics-row {
          grid-template-columns: 1fr;
        }
        .form-grid {
          grid-template-columns: 1fr;
        }
        .page-header {
          flex-wrap: wrap;
          gap: var(--space-3);
        }
      }
    `
  ];

  constructor() {
    super();
    this.viewMode = 'grid';
    this.selectedAgent = null;
    this.activeTab = 'overview';
    this._toastVisible = false;
    this.agents = [
      {
        id: 1,
        name: 'Park Receptionist',
        color: 'orange',
        status: 'active',
        totalCalls: 1483,
        successRate: 94.2,
        avgRating: 4.8,
        lastActive: '2h ago',
        language: 'EN-US',
        voiceName: 'Alloy',
        description: 'Primary front desk agent for incoming reservation and general inquiries.',
        systemPrompt: 'You are a friendly, professional park receptionist. Help callers with reservations, park amenities, directions, and general questions. Always be warm and welcoming.',
        tone: 'professional',
        greeting: 'Hello! Thank you for calling Hickory Hills RV Park. How may I help you today?',
        afterHoursMessage: 'Thank you for calling. Our office is currently closed. Office hours are Monday through Friday, 8 AM to 6 PM, and weekends 9 AM to 5 PM. Please call back during business hours.',
        businessHours: {
          Mon: { enabled: true, start: '08:00', end: '18:00' },
          Tue: { enabled: true, start: '08:00', end: '18:00' },
          Wed: { enabled: true, start: '08:00', end: '18:00' },
          Thu: { enabled: true, start: '08:00', end: '18:00' },
          Fri: { enabled: true, start: '08:00', end: '18:00' },
          Sat: { enabled: true, start: '09:00', end: '17:00' },
          Sun: { enabled: true, start: '09:00', end: '17:00' }
        },
        maxDuration: 600,
        transferNumber: '+18005551234',
        interruptionHandling: 'allow',
        recentCalls: [
          { number: '+1 (555) 234-8901', time: 'Today, 2:15 PM', duration: '4:32', status: 'success' },
          { number: '+1 (555) 876-5432', time: 'Today, 1:48 PM', duration: '2:17', status: 'success' },
          { number: '+1 (555) 111-2233', time: 'Today, 12:30 PM', duration: '0:12', status: 'missed' },
          { number: '+1 (555) 987-6543', time: 'Today, 11:05 AM', duration: '6:45', status: 'success' },
          { number: '+1 (555) 444-5566', time: 'Yesterday, 4:52 PM', duration: '3:18', status: 'success' }
        ],
        activity: [
          { text: 'Handled reservation inquiry from +1 (555) 234-8901', time: '2 hours ago' },
          { text: 'Completed call with +1 (555) 876-5432 about amenities', time: '2.5 hours ago' },
          { text: 'Missed call from +1 (555) 111-2233', time: '3.5 hours ago' },
          { text: 'System prompt updated by admin', time: '1 day ago' },
          { text: 'Business hours modified', time: '3 days ago' }
        ]
      },
      {
        id: 2,
        name: 'After Hours Bot',
        color: 'blue',
        status: 'active',
        totalCalls: 621,
        successRate: 89.7,
        avgRating: 4.5,
        lastActive: '6h ago',
        language: 'EN-US',
        voiceName: 'Nova',
        description: 'Handles after-hours calls with message taking and emergency routing.',
        systemPrompt: 'You are an after-hours answering service. Take messages, handle emergencies, and let callers know when staff will be available.',
        tone: 'friendly',
        greeting: 'Hi! You have reached us after office hours. I can take a message or help with urgent matters.',
        afterHoursMessage: '',
        businessHours: {
          Mon: { enabled: true, start: '18:00', end: '08:00' },
          Tue: { enabled: true, start: '18:00', end: '08:00' },
          Wed: { enabled: true, start: '18:00', end: '08:00' },
          Thu: { enabled: true, start: '18:00', end: '08:00' },
          Fri: { enabled: true, start: '18:00', end: '08:00' },
          Sat: { enabled: true, start: '17:00', end: '09:00' },
          Sun: { enabled: true, start: '17:00', end: '09:00' }
        },
        maxDuration: 300,
        transferNumber: '+18005559999',
        interruptionHandling: 'queue',
        recentCalls: [
          { number: '+1 (555) 321-0987', time: 'Today, 7:30 AM', duration: '2:15', status: 'success' },
          { number: '+1 (555) 654-3210', time: 'Yesterday, 11:45 PM', duration: '1:48', status: 'success' }
        ],
        activity: [
          { text: 'Took message from +1 (555) 321-0987', time: '6 hours ago' },
          { text: 'Handled late-night call from +1 (555) 654-3210', time: '14 hours ago' }
        ]
      },
      {
        id: 3,
        name: 'Spanish Agent',
        color: 'green',
        status: 'active',
        totalCalls: 387,
        successRate: 91.5,
        avgRating: 4.6,
        lastActive: '1d ago',
        language: 'ES-MX',
        voiceName: 'Shimmer',
        description: 'Spanish-speaking agent for bilingual callers and Latin American guests.',
        systemPrompt: 'Eres un recepcionista amigable y profesional. Ayuda a las personas con reservaciones, servicios del parque, e informacion general. Siempre se calido y acogedor.',
        tone: 'friendly',
        greeting: 'Hola! Gracias por llamar a Hickory Hills RV Park. Como le puedo ayudar?',
        afterHoursMessage: 'Gracias por llamar. Nuestra oficina esta cerrada. Horario de atencion es de lunes a viernes, 8 AM a 6 PM.',
        businessHours: {
          Mon: { enabled: true, start: '08:00', end: '18:00' },
          Tue: { enabled: true, start: '08:00', end: '18:00' },
          Wed: { enabled: true, start: '08:00', end: '18:00' },
          Thu: { enabled: true, start: '08:00', end: '18:00' },
          Fri: { enabled: true, start: '08:00', end: '18:00' },
          Sat: { enabled: false, start: '', end: '' },
          Sun: { enabled: false, start: '', end: '' }
        },
        maxDuration: 600,
        transferNumber: '+18005551234',
        interruptionHandling: 'allow',
        recentCalls: [
          { number: '+1 (555) 789-0123', time: 'Yesterday, 3:20 PM', duration: '5:42', status: 'success' }
        ],
        activity: [
          { text: 'Completed call with +1 (555) 789-0123', time: '1 day ago' }
        ]
      },
      {
        id: 4,
        name: 'Test Agent',
        color: 'purple',
        status: 'inactive',
        totalCalls: 42,
        successRate: 78.3,
        avgRating: 3.9,
        lastActive: '5d ago',
        language: 'EN-US',
        voiceName: 'Echo',
        description: 'Development and testing agent for QA purposes.',
        systemPrompt: 'You are a test agent. Respond to all queries with helpful information for testing purposes.',
        tone: 'casual',
        greeting: 'Hey! This is the test line. What would you like to try?',
        afterHoursMessage: 'Test line is offline.',
        businessHours: {
          Mon: { enabled: true, start: '09:00', end: '17:00' },
          Tue: { enabled: true, start: '09:00', end: '17:00' },
          Wed: { enabled: true, start: '09:00', end: '17:00' },
          Thu: { enabled: true, start: '09:00', end: '17:00' },
          Fri: { enabled: true, start: '09:00', end: '17:00' },
          Sat: { enabled: false, start: '', end: '' },
          Sun: { enabled: false, start: '', end: '' }
        },
        maxDuration: 120,
        transferNumber: '',
        interruptionHandling: 'allow',
        recentCalls: [],
        activity: [
          { text: 'Agent deactivated by admin', time: '5 days ago' }
        ]
      }
    ];
  }

  _selectAgent(agent) {
    this.selectedAgent = agent;
    this.activeTab = 'overview';
  }

  _backToGrid() {
    this.selectedAgent = null;
  }

  _setTab(tab) {
    this.activeTab = tab;
  }

  _setViewMode(mode) {
    this.viewMode = mode;
  }

  _showToast(message) {
    this._toastMessage = message;
    this._toastVisible = true;
    setTimeout(() => { this._toastVisible = false; }, 3000);
  }

  render() {
    return html`
      ${iconStyles}
      ${this.selectedAgent ? this._renderDetailPanel() : this._renderGrid()}
      <div class="toast ${this._toastVisible ? 'visible' : ''}">
        <i class="fa-sharp fa-light fa-check"></i>
        ${this._toastMessage || 'Saved successfully'}
      </div>
    `;
  }

  /* ─── Grid View ─── */
  _renderGrid() {
    return html`
      <div class="page-header">
        <h1 class="page-title">AI Agents</h1>
        <div class="header-actions">
          <div class="view-toggle">
            <button
              class="view-toggle-btn ${this.viewMode === 'grid' ? 'active' : ''}"
              @click=${() => this._setViewMode('grid')}
              title="Grid view"
            >
              <i class="fa-sharp fa-light fa-grid-2"></i>
            </button>
            <button
              class="view-toggle-btn ${this.viewMode === 'list' ? 'active' : ''}"
              @click=${() => this._setViewMode('list')}
              title="List view"
            >
              <i class="fa-sharp fa-light fa-list"></i>
            </button>
          </div>
          <button class="btn btn-primary">
            <i class="fa-sharp fa-light fa-plus"></i>
            Create Agent
          </button>
        </div>
      </div>

      <div class="agents-grid ${this.viewMode === 'list' ? 'list-view' : ''}">
        ${this.agents.map(agent => this._renderAgentCard(agent))}
      </div>
    `;
  }

  _renderAgentCard(agent) {
    return html`
      <div class="agent-card" @click=${() => this._selectAgent(agent)}>
        <div class="agent-card-header">
          <div class="agent-avatar color-${agent.color}">
            <i class="fa-sharp-duotone fa-solid fa-robot"></i>
          </div>
          <div class="agent-info">
            <div class="agent-name">${agent.name}</div>
            <div class="agent-meta">
              <span class="badge ${agent.status === 'active' ? 'badge-success' : 'badge-neutral'}">
                <span class="status-dot ${agent.status === 'active' ? 'active' : 'inactive'}"></span>
                ${agent.status === 'active' ? 'Active' : 'Inactive'}
              </span>
              <span class="badge badge-neutral">${agent.language}</span>
            </div>
          </div>
        </div>

        <div class="agent-stats-row">
          <div class="agent-stat-item">
            <span class="stat-value">${agent.totalCalls.toLocaleString()}</span>
            <span class="stat-label">Total Calls</span>
          </div>
          <div class="agent-stat-item">
            <span class="stat-value">${agent.successRate}%</span>
            <span class="stat-label">Success Rate</span>
          </div>
          <div class="agent-stat-item">
            <span class="stat-value">${agent.avgRating}</span>
            <span class="stat-label">Avg Rating</span>
          </div>
        </div>

        <div class="agent-card-footer">
          <span class="last-active">Last active: ${agent.lastActive}</span>
          <span class="voice-name">
            <i class="fa-sharp fa-light fa-waveform-lines" style="font-size: 10px;"></i>
            ${agent.voiceName}
          </span>
        </div>
      </div>
    `;
  }

  /* ─── Detail Panel ─── */
  _renderDetailPanel() {
    const agent = this.selectedAgent;
    return html`
      <div class="detail-panel">
        <button class="detail-back-btn" @click=${this._backToGrid}>
          <i class="fa-sharp fa-light fa-arrow-left"></i>
          Back to Agents
        </button>

        <div class="detail-header">
          <div class="detail-avatar color-${agent.color}">
            <i class="fa-sharp-duotone fa-solid fa-robot"></i>
          </div>
          <div class="detail-title-area">
            <div class="detail-title">${agent.name}</div>
            <div class="detail-subtitle">
              <span class="badge ${agent.status === 'active' ? 'badge-success' : 'badge-neutral'}" style="margin-right: var(--space-2);">
                <span class="status-dot ${agent.status === 'active' ? 'active' : 'inactive'}"></span>
                ${agent.status === 'active' ? 'Active' : 'Inactive'}
              </span>
              ${agent.description}
            </div>
          </div>
        </div>

        <div class="detail-tabs">
          ${['overview', 'configuration', 'knowledge', 'testing', 'activity'].map(tab => html`
            <button
              class="detail-tab ${this.activeTab === tab ? 'active' : ''}"
              @click=${() => this._setTab(tab)}
            >
              ${tab === 'overview' ? 'Overview' : ''}
              ${tab === 'configuration' ? 'Configuration' : ''}
              ${tab === 'knowledge' ? 'Knowledge Base' : ''}
              ${tab === 'testing' ? 'Testing' : ''}
              ${tab === 'activity' ? 'Activity' : ''}
            </button>
          `)}
        </div>

        ${this.activeTab === 'overview' ? this._renderOverviewTab(agent) : ''}
        ${this.activeTab === 'configuration' ? this._renderConfigTab(agent) : ''}
        ${this.activeTab === 'knowledge' ? this._renderKnowledgeTab() : ''}
        ${this.activeTab === 'testing' ? this._renderTestingTab(agent) : ''}
        ${this.activeTab === 'activity' ? this._renderActivityTab(agent) : ''}
      </div>
    `;
  }

  /* ─── Overview Tab ─── */
  _renderOverviewTab(agent) {
    return html`
      <div class="metrics-row">
        <div class="metric-card">
          <div class="metric-label">Total Calls</div>
          <div class="metric-value">${agent.totalCalls.toLocaleString()}</div>
          <div class="metric-change positive">
            <i class="fa-sharp fa-light fa-arrow-up" style="font-size:10px;"></i> 12.3%
          </div>
        </div>
        <div class="metric-card">
          <div class="metric-label">Success Rate</div>
          <div class="metric-value">${agent.successRate}%</div>
          <div class="metric-change positive">
            <i class="fa-sharp fa-light fa-arrow-up" style="font-size:10px;"></i> 2.1%
          </div>
        </div>
        <div class="metric-card">
          <div class="metric-label">Avg Rating</div>
          <div class="metric-value">${agent.avgRating}/5</div>
          <div class="metric-change positive">
            <i class="fa-sharp fa-light fa-arrow-up" style="font-size:10px;"></i> 0.3
          </div>
        </div>
        <div class="metric-card">
          <div class="metric-label">Avg Duration</div>
          <div class="metric-value">3:42</div>
          <div class="metric-change negative">
            <i class="fa-sharp fa-light fa-arrow-down" style="font-size:10px;"></i> 0:15
          </div>
        </div>
      </div>

      <!-- Recent Calls -->
      <div class="recent-calls-section">
        <div class="section-header">
          <span class="section-title">Recent Calls</span>
          <button class="btn btn-ghost btn-sm">View All</button>
        </div>
        ${agent.recentCalls.length > 0 ? agent.recentCalls.map(call => html`
          <div class="call-list-item">
            <div class="call-icon-wrap ${call.status}">
              <i class="fa-sharp fa-light ${call.status === 'success' ? 'fa-phone' : 'fa-phone-missed'}"></i>
            </div>
            <div class="call-info">
              <div class="call-number">${call.number}</div>
              <div class="call-time">${call.time}</div>
            </div>
            <div class="call-duration">${call.duration}</div>
          </div>
        `) : html`
          <div class="empty-state" style="padding: var(--space-8);">
            <div class="empty-state-icon"><i class="fa-sharp fa-light fa-phone-slash"></i></div>
            <div class="empty-state-title">No recent calls</div>
            <div class="empty-state-description">This agent has not handled any calls yet.</div>
          </div>
        `}
      </div>

      <!-- Activity Timeline -->
      <div class="recent-calls-section">
        <div class="section-header">
          <span class="section-title">Activity Timeline</span>
        </div>
        <div class="timeline">
          ${agent.activity.map(item => html`
            <div class="timeline-item">
              <div class="timeline-content">${item.text}</div>
              <div class="timeline-time">${item.time}</div>
            </div>
          `)}
        </div>
      </div>
    `;
  }

  /* ─── Configuration Tab ─── */
  _renderConfigTab(agent) {
    return html`
      <!-- Basic Section -->
      <div class="config-section">
        <div class="config-section-title">
          <i class="fa-sharp fa-light fa-id-card" style="margin-right:var(--space-2);color:var(--color-primary);"></i>
          Basic Information
        </div>
        <div class="config-section-desc">Core details and identity for this agent.</div>

        <div class="form-grid">
          <div class="form-group">
            <label class="form-label">Agent Name</label>
            <input type="text" class="form-input" .value=${agent.name} />
          </div>
          <div class="form-group">
            <label class="form-label">Language</label>
            <select class="form-select">
              <option value="en-US" ?selected=${agent.language === 'EN-US'}>English (US)</option>
              <option value="en-GB">English (UK)</option>
              <option value="es-MX" ?selected=${agent.language === 'ES-MX'}>Spanish (MX)</option>
              <option value="fr-FR">French</option>
            </select>
          </div>
        </div>
        <div class="form-grid full" style="margin-top:var(--space-4);">
          <div class="form-group">
            <label class="form-label">Description</label>
            <textarea class="form-textarea" style="min-height:70px;" .value=${agent.description}></textarea>
          </div>
        </div>
        <div class="form-grid" style="margin-top:var(--space-4);">
          <div class="form-group">
            <label class="form-label">Voice</label>
            <div class="input-with-btn">
              <select class="form-select">
                <option value="alloy" ?selected=${agent.voiceName === 'Alloy'}>Alloy</option>
                <option value="echo" ?selected=${agent.voiceName === 'Echo'}>Echo</option>
                <option value="nova" ?selected=${agent.voiceName === 'Nova'}>Nova</option>
                <option value="shimmer" ?selected=${agent.voiceName === 'Shimmer'}>Shimmer</option>
                <option value="fable">Fable</option>
                <option value="onyx">Onyx</option>
              </select>
              <button class="play-btn" title="Preview voice">
                <i class="fa-sharp fa-light fa-play"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Personality Section -->
      <div class="config-section">
        <div class="config-section-title">
          <i class="fa-sharp fa-light fa-brain" style="margin-right:var(--space-2);color:var(--color-info);"></i>
          Personality
        </div>
        <div class="config-section-desc">Define how the agent speaks and behaves on calls.</div>

        <div class="form-grid full">
          <div class="form-group">
            <label class="form-label">System Prompt</label>
            <textarea
              class="form-textarea"
              style="min-height:120px;font-family:var(--font-mono);font-size:var(--text-xs);"
              .value=${agent.systemPrompt}
              @input=${(e) => this._updateCharCount(e)}
            ></textarea>
            <div class="char-count">${(agent.systemPrompt || '').length} / 4000 characters</div>
          </div>
        </div>
        <div class="form-grid" style="margin-top:var(--space-4);">
          <div class="form-group">
            <label class="form-label">Tone</label>
            <select class="form-select">
              <option value="professional" ?selected=${agent.tone === 'professional'}>Professional</option>
              <option value="friendly" ?selected=${agent.tone === 'friendly'}>Friendly</option>
              <option value="casual" ?selected=${agent.tone === 'casual'}>Casual</option>
              <option value="formal" ?selected=${agent.tone === 'formal'}>Formal</option>
            </select>
          </div>
          <div class="form-group">
            <label class="form-label">Greeting Message</label>
            <input type="text" class="form-input" .value=${agent.greeting} />
          </div>
        </div>
        <div class="form-grid full" style="margin-top:var(--space-4);">
          <div class="form-group">
            <label class="form-label">After-Hours Message</label>
            <input type="text" class="form-input" .value=${agent.afterHoursMessage} placeholder="Message when calling outside business hours" />
          </div>
        </div>
      </div>

      <!-- Business Hours Section -->
      <div class="config-section">
        <div class="config-section-title">
          <i class="fa-sharp fa-light fa-clock" style="margin-right:var(--space-2);color:var(--color-warning);"></i>
          Business Hours
        </div>
        <div class="config-section-desc">Set when the agent is available to receive calls.</div>

        <div class="hours-grid">
          ${Object.entries(agent.businessHours).map(([day, hours]) => html`
            <div class="hours-row">
              <div
                class="toggle ${hours.enabled ? 'active' : ''}"
                @click=${() => this._toggleDay(day)}
              ></div>
              <span class="hours-day">${day}</span>
              <input
                type="time"
                class="time-input"
                .value=${hours.start}
                ?disabled=${!hours.enabled}
              />
              <input
                type="time"
                class="time-input"
                .value=${hours.end}
                ?disabled=${!hours.enabled}
              />
            </div>
          `)}
        </div>
      </div>

      <!-- Advanced Section -->
      <div class="config-section">
        <div class="config-section-title">
          <i class="fa-sharp fa-light fa-sliders" style="margin-right:var(--space-2);color:var(--color-text-tertiary);"></i>
          Advanced
        </div>
        <div class="config-section-desc">Fine-tune agent behavior and limits.</div>

        <div class="form-grid full">
          <div class="form-group">
            <div class="slider-group">
              <div class="slider-header">
                <label class="form-label">Max Call Duration</label>
                <span class="slider-value">${Math.floor(agent.maxDuration / 60)}m ${agent.maxDuration % 60}s</span>
              </div>
              <input
                type="range"
                min="60"
                max="1800"
                step="30"
                .value=${String(agent.maxDuration)}
              />
              <div class="form-hint">Maximum time before the call is automatically ended.</div>
            </div>
          </div>
        </div>
        <div class="form-grid" style="margin-top:var(--space-4);">
          <div class="form-group">
            <label class="form-label">Transfer Number</label>
            <input type="tel" class="form-input" .value=${agent.transferNumber} placeholder="+1 (555) 000-0000" />
            <div class="form-hint">Phone number for live agent transfer.</div>
          </div>
          <div class="form-group">
            <label class="form-label">Interruption Handling</label>
            <select class="form-select">
              <option value="allow" ?selected=${agent.interruptionHandling === 'allow'}>Allow Interruptions</option>
              <option value="queue" ?selected=${agent.interruptionHandling === 'queue'}>Queue Until Finished</option>
              <option value="ignore" ?selected=${agent.interruptionHandling === 'ignore'}>Ignore Interruptions</option>
            </select>
            <div class="form-hint">How the agent handles caller interruptions mid-speech.</div>
          </div>
        </div>
      </div>

      <div style="display:flex;justify-content:flex-end;gap:var(--space-3);padding-top:var(--space-4);border-top:1px solid var(--color-border-primary);margin-top:var(--space-4);">
        <button class="btn btn-secondary" @click=${this._backToGrid}>Cancel</button>
        <button class="btn btn-primary" @click=${() => this._showToast('Agent configuration saved')}>
          <i class="fa-sharp fa-light fa-floppy-disk"></i>
          Save Changes
        </button>
      </div>
    `;
  }

  /* ─── Knowledge Base Tab ─── */
  _renderKnowledgeTab() {
    return html`
      <div class="empty-state">
        <div class="empty-state-icon"><i class="fa-sharp fa-light fa-books"></i></div>
        <div class="empty-state-title">Knowledge Base</div>
        <div class="empty-state-description">Upload documents, FAQs, and reference materials that the agent can use during calls.</div>
        <button class="btn btn-primary" style="margin-top:var(--space-4);">
          <i class="fa-sharp fa-light fa-upload"></i>
          Upload Documents
        </button>
      </div>
    `;
  }

  /* ─── Testing Tab ─── */
  _renderTestingTab(agent) {
    return html`
      <div class="empty-state">
        <div class="empty-state-icon"><i class="fa-sharp fa-light fa-vial"></i></div>
        <div class="empty-state-title">Test ${agent.name}</div>
        <div class="empty-state-description">Start a test conversation to evaluate the agent's behavior and responses before going live.</div>
        <button class="btn btn-primary" style="margin-top:var(--space-4);">
          <i class="fa-sharp fa-light fa-phone"></i>
          Start Test Call
        </button>
      </div>
    `;
  }

  /* ─── Activity Tab ─── */
  _renderActivityTab(agent) {
    return html`
      <div class="recent-calls-section">
        <div class="section-header">
          <span class="section-title">Full Activity Log</span>
        </div>
        <div class="timeline">
          ${agent.activity.map(item => html`
            <div class="timeline-item">
              <div class="timeline-content">${item.text}</div>
              <div class="timeline-time">${item.time}</div>
            </div>
          `)}
        </div>
      </div>
    `;
  }

  _toggleDay(day) {
    const agent = { ...this.selectedAgent };
    const hours = { ...agent.businessHours };
    hours[day] = { ...hours[day], enabled: !hours[day].enabled };
    agent.businessHours = hours;
    this.selectedAgent = agent;
  }

  _updateCharCount(e) {
    // Reactive via Lit property tracking
  }
}

customElements.define('agents-manager', AgentsManager);
